import React from 'react';
import classnames from 'classnames';
import { Activities, attraqtTracker } from '../../../../../analytics/attraqt/attraqtTracker';
import productStyles from './product.module.pcss';
import productsStyles from '../products.module.pcss';
import { ListerProductData } from '../../../../../types';
import Schema from './components/schema';
import FavouritesButton from './components/favouritesButton';
import Image from './components/image';
import { FavouritesToggler } from '../hooks/useFavouritesHandler';
import isTouchSupported from '../../../../../helpers/isTouchSupported';
import Description from './components/description';
import getPressedKey, { KeyboardMap } from '../../../../../helpers/getPressedKey';
import { writeClickedProductInfo, useFocusOnLastVisitedProduct } from '../../pagination/productClickHooks';

interface ProductProps {
  showColorSwatchPlaceholder: boolean;
  showIfcMessagePlaceholder: boolean;
  product: ListerProductData;
  handleFavouritesClick: FavouritesToggler;
  index: number;
  isFavourite: boolean;
  rid: string;
}

const isTouch = isTouchSupported();

function Product({
  showColorSwatchPlaceholder,
  showIfcMessagePlaceholder,
  product,
  handleFavouritesClick,
  index,
  isFavourite,
  rid
}: ProductProps): React.ReactElement {
  const { url, imageUrl, amplienceRoundel, manufacturer, code } = product;

  const productRef = useFocusOnLastVisitedProduct(product.code);

  // NOTE: we need to track on onMouseDown and onEnterKey events,
  // because onClick won't handle open in the new tab or open with middle click
  function handleLinkMouseDown(): void {
    writeClickedProductInfo({ index, code: product.code });
    attraqtTracker({ action: Activities.CLICK, target: { product: code }, sourceId: rid }, true);
  }

  function handleLinkEnter(e: React.KeyboardEvent<HTMLAnchorElement>): void {
    if (getPressedKey(e) === KeyboardMap.ENTER) {
      writeClickedProductInfo({ index, code: product.code });
      attraqtTracker({ action: Activities.CLICK, target: { product: code }, sourceId: rid }, true);
    }
  }

  return (
    <div data-product-wrapper ref={productRef}>
      <div className={productStyles.product} itemScope itemType="http://schema.org/Product">
        <Schema brand={manufacturer} />
        <div
          className={classnames(
            productStyles['product__buttons-wrapper'],
            productsStyles['product__buttons-wrapper'],
            isTouch && productStyles['product__buttons-wrapper--touch']
          )}
        >
          <FavouritesButton
            isFavourite={isFavourite}
            productCode={code}
            handleFavouritesClick={handleFavouritesClick}
          />
        </div>
        <a
          onMouseDown={handleLinkMouseDown}
          onKeyDown={handleLinkEnter}
          className={productStyles.product__link}
          href={url || ''}
          itemScope
          itemType="http://schema.org/Product"
        >
          <Image
            url={url}
            imageUrl={imageUrl}
            amplienceRoundel={amplienceRoundel}
            className={productStyles['product__image-wrapper']}
          />
          <Description
            showColorSwatchPlaceholder={showColorSwatchPlaceholder}
            showIfcMessagePlaceholder={showIfcMessagePlaceholder}
            product={product}
          />
        </a>
      </div>
    </div>
  );
}

export default Product;
